import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import {
  ClassifierListItem,
  UpdateClassifierRequest,
  CreateClassifierValueRequest,
  ClassifierCard,
  ClassifierValueCard,
  ClassifierValueByIdCard,
  UpdateClassifierValueRequest
} from 'types/classifier.interface';
import { DeleteClassifierValueComponent } from '../modal/delete-classifier-value/delete-classifier-value.component';
import { AddClassifierValueComponent } from '../modal/add-classifier-value/add-classifier-value.component';
import { EditClassifierComponent } from '../modal/edit-classifier/edit-classifier.component';
import { EditClassifierValueComponent } from 'components/modal/edit-classifier-value/edit-classifier-value.component';
import { ApiService } from 'services/api.service';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ToastService } from 'services/toast.service';
import { CLAIM_TYPES, CLAIM_SCOPES } from 'types/user.interface';
import { ClaimDirective } from 'src/app/directive/claim.directive';

@Component({
  selector: 'app-classifier',
  standalone: true,
  imports: [
    NgbCollapse,
    NgClass,
    DeleteClassifierValueComponent,
    AddClassifierValueComponent,
    EditClassifierComponent,
    EditClassifierValueComponent,
    SpinnerComponent,
    ClaimDirective
  ],
  templateUrl: './classifier.component.html',
  styleUrl: './classifier.component.scss'
})
export class ClassifierComponent implements OnChanges {
  public readonly CLAIM_SCOPE = CLAIM_SCOPES['classifier'];
  public readonly CLAIM_TYPES = CLAIM_TYPES;

  @Input() classifier!: ClassifierListItem;
  @Input() valueCount!: number;
  @Input() isClassifierCrudLoading: boolean = false;
  @Output() classifierEdit: EventEmitter<UpdateClassifierRequest> = new EventEmitter<UpdateClassifierRequest>();
  @Output() classifierValueAdd: EventEmitter<CreateClassifierValueRequest> = new EventEmitter<CreateClassifierValueRequest>();
  @Output() classifierValueEdit: EventEmitter<UpdateClassifierValueRequest> = new EventEmitter<UpdateClassifierValueRequest>();

  classifierValues: ClassifierValueCard[] = [];
  isCollapsed: boolean = true;
  haveValuesLoaded: boolean = false;
  educationLevel = 'EducationLevel';
  scientificDegree = 'ScientificDegree';
  // Protected by the immutable value code, not the (renameable) name — renaming a system
  // value must not make it deletable (LNA-1959). Name sets are kept only as a fallback for
  // seeded values that may have no code.
  private readonly PROTECTED_VALUE_CODES: Record<string, Set<string>> = {
    InformationRequestTaskType: new Set(['InformationRequestTaskType.PrepareInvoice']),
    AtbildesDokumentaVeids: new Set(['AtbildesDokumentaVeids.DocumentCopy'])
  };
  private readonly PROTECTED_VALUE_NAMES: Record<string, Set<string>> = {
    InformationRequestTaskType: new Set(['Sagatavot rēķinu']),
    AtbildesDokumentaVeids: new Set(['Dokumenta kopija'])
  };
  constructor(
    private apiService: ApiService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const countChange: SimpleChange = changes['valueCount'];

    if (
      countChange &&
      countChange.currentValue &&
      countChange.previousValue &&
      this.haveValuesLoaded &&
      countChange.currentValue != countChange.previousValue
    ) {
      this.haveValuesLoaded = false;
      this.getClassifierValues();
    }
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;

    if (!this.haveValuesLoaded) {
      this.getClassifierValues();
    }
  }

  getClassifierValues(): void {
    if (this.classifier.code) {
      this.apiService.getClassifierValues(this.classifier.code).subscribe(
        (classifier: ClassifierCard) => {
          if (classifier.values) {
            this.classifierValues = classifier.values;
            this.classifierValues.sort((a: ClassifierValueCard, b: ClassifierValueCard) => a.name!.localeCompare(b.name!));
          }
          this.haveValuesLoaded = true;
        },
        (error: any) => {
          console.error('Error loading classifier values:', error);
        }
      );
    }
  }

  onClassifierEdit(classifier: UpdateClassifierRequest): void {
    this.classifierEdit.emit(classifier);
  }

  onAddClassifierValue(value: CreateClassifierValueRequest): void {
    this.classifierValueAdd.emit(value);
  }

  editClassifierValue(classifierValue: UpdateClassifierValueRequest): void {
    this.isClassifierCrudLoading = true;

    this.apiService.editClassifierValue(classifierValue).subscribe(
      (value: ClassifierValueByIdCard) => {
        this.isClassifierCrudLoading = false;
        this.toastService.showSuccess('editClassifierSuccess', '', {
          shouldTranslate: true
        });

        const index: number = this.classifierValues.findIndex((v: ClassifierValueCard) => v.id == value.id) || -1;
        if (index !== -1) {
          this.classifierValues[index].name = value.name;
          this.classifierValues.sort((a: ClassifierValueCard, b: ClassifierValueCard) => a.name!.localeCompare(b.name!));
        }
      },
      (error: any) => {
        console.error('Error Editing classifier value:', error);
      }
    );
  }

  allowedToModify(item: ClassifierListItem): boolean {
    const restricted = new Set([this.educationLevel, this.scientificDegree].filter((x): x is string => typeof x === 'string'));
    return !restricted.has(item.code ?? '');
  }

  isDeleteRestricted(value: ClassifierValueCard): boolean {
    const classifierCode = this.classifier.code ?? '';
    const protectedCodes = this.PROTECTED_VALUE_CODES[classifierCode];
    if (protectedCodes && value.code && protectedCodes.has(value.code)) {
      return true;
    }
    const protectedNames = this.PROTECTED_VALUE_NAMES[classifierCode];
    return !!protectedNames && protectedNames.has(value.name);
  }

  deleteClassifierValue(classifierId: string): void {
    this.isClassifierCrudLoading = true;

    this.apiService.deleteClassifierValue(classifierId).subscribe(
      () => {
        // Drop the value from the local list too — otherwise it keeps blocking the
        // uniqueness check (and stays rendered) until a full page reload.
        this.classifierValues = this.classifierValues.filter((v: ClassifierValueCard) => v.id !== classifierId);
        this.valueCount = this.valueCount - 1;
        this.classifier.count = this.valueCount;
        this.isClassifierCrudLoading = false;
        this.toastService.showSuccess('deleteClassifierSuccess', '', {
          shouldTranslate: true
        });
      },
      (error: any) => {
        this.toastService.showRequestError();
        this.isClassifierCrudLoading = false;
        console.error('Error Deleting classifier value:', error);
      }
    );
  }
}
